import React, { useState } from "react";
import { DataTable, DataTableRowEditCompleteParams } from "primereact/datatable";
import ColorPicker from "components/basic/color-picker/ColorPicker";
import { T } from "services/lang/LanguageService";
import { Column, ColumnEditorOptions } from "primereact/column";
import DatasetTitle from "components/basic/display-titles/DatasetTitle";
import { InputText } from "primereact/inputtext";
import { YAxisIdentifier, YAxisIdentifierWithOverriddenTitle } from "./interfaces";

interface ChartIdentifiersEditorProps {
  currentConfig: YAxisIdentifier[];
  setCurrentConfig: (config: YAxisIdentifier[]) => void;
  chartType?: string;
}

const ChartIdentifiersEditor = ({
  currentConfig,
  setCurrentConfig,
  chartType
}: ChartIdentifiersEditorProps) => {
  const [identifiers, setIdentifiers] = useState<YAxisIdentifier[]>(
    currentConfig.map(item =>
      typeof item === "string" ? { identifier: item, overriddenTitle: "" } : item
    )
  );

  const titleBodyTemplate = (rowData: YAxisIdentifierWithOverriddenTitle) => {
    return <DatasetTitle identifier={rowData.identifier} />;
  };

  const textEditor = (options: ColumnEditorOptions) => {
    return (
      <InputText
        value={options.value || ""}
        onChange={e => options.editorCallback?.(e.target.value)}
        style={{ width: "100%" }}
      />
    );
  };

  const handleEditorCallback = (
    options: ColumnEditorOptions,
    newLine: Partial<YAxisIdentifierWithOverriddenTitle>
  ) => {
    if (typeof options.rowData === "string") {
      return;
    }

    const updatedIdentifiers = identifiers.map(item =>
      typeof item !== "string" && item.identifier === options.rowData.identifier
        ? { ...item, ...newLine }
        : item
    );

    setIdentifiers(updatedIdentifiers);
    setCurrentConfig(updatedIdentifiers);
  };

  const colorEditor = (options: ColumnEditorOptions) => {
    const rowData = options.rowData as YAxisIdentifierWithOverriddenTitle;
    const value = rowData.color ?? "#FFFFFF";

    return (
      <div className="flex gap-1 align-items-center">
        <ColorPicker
          value={value}
          onValueChange={e => {
            const color = e.target.value;
            options.editorCallback?.(color);
            handleEditorCallback(options, { color });
          }}
        />
        <i
          className="pi pi-refresh ml-2 cursor-pointer"
          onClick={() => handleEditorCallback(options, { color: undefined })}
        />
      </div>
    );
  };

  const colorBodyTemplate = (rowData: YAxisIdentifier) => {
    let color = "#FFFFFF";

    if (typeof rowData !== "string" && rowData.color) {
      color = rowData.color;
    }

    return (
      <div
        style={{
          width: "25px",
          height: "25px",
          backgroundColor: color.startsWith("#") ? color : `#${color}`,
          borderRadius: "5px",
          border: "1px solid #c8c8c8",
        }}
      />
    );
  };

  const overriddenTitleBodyTemplate = (rowData: YAxisIdentifierWithOverriddenTitle) => {
    return <div>{rowData.overriddenTitle}</div>;
  };

  const onRowEditComplete = (e: DataTableRowEditCompleteParams): void => {
    const { newData, index } = e;
    if (newData && index !== undefined && index !== null) {
      const updatedIdentifiers = [...identifiers];
      updatedIdentifiers[index] = { ...newData };
      setIdentifiers(updatedIdentifiers);
      setCurrentConfig(updatedIdentifiers);
    } else {
      console.error("Invalid edit event data:", e);
    }
  };

  const handleRowReorderEvt = (e: any) => {
    const reorderedData = [...identifiers];
    const [removed] = reorderedData.splice(e.from, 1);
    reorderedData.splice(e.to, 0, removed);
    setIdentifiers(reorderedData);
    setCurrentConfig(reorderedData);
  };

  return (
    <div>
      <DataTable
        value={identifiers}
        onRowEditComplete={onRowEditComplete}
        editMode="row"
        onRowReorder={handleRowReorderEvt}
        reorderableRows
      >
        <Column
          field="identifier"
          header={T("word.title")}
          body={titleBodyTemplate}
          style={{ width: "50%" }}
        />
        <Column
          className="p-0"
          field="overriddenTitle"
          header={T("word.overridden-title")}
          alignHeader="left"
          style={{ width: "30%", textAlign: "left" }}
          align="center"
          editor={options => textEditor(options)}
          body={overriddenTitleBodyTemplate}
        />
        {chartType === "PIE" && <Column
          field="color"
          header={T("word.color")}
          editor={options => colorEditor(options)}
          body={colorBodyTemplate}
        />}
        
        <Column rowEditor align="center" style={{ width: "10%" }} />
      </DataTable>
    </div>
  );
};

export default ChartIdentifiersEditor;
